// One proposed write, reviewed on its own: an editable control per changed
// field with the change shown inline (word diff for text, +/− delta for
// multiselects, "was:" hint for selects), plus a "Respond to the agent" box
// that routes the item to a revision request.
import React, { useMemo } from 'react';
import StylishNewTextArea from 'components/DesignSystems/New/StylishNewTextArea';
import {
  getArgValue,
  getFieldConfig,
  getFieldLabel,
  humanizeFieldName,
  isPlainObject,
  optionLabel,
  type FieldConfig,
  type SelectOption,
} from '../../confirmationFieldConfig';
import type { ItemState, PendingConfirmation, ReviewUnit } from '../types';
import ConfirmationField, {
  asEditableText,
  formatValue,
} from './ConfirmationField';
import InlineDiffText from './InlineDiffText';

/**
 * Display a value by its option label when it resolves (selects/multiselects
 * whose stored value is an id — e.g. a member GUID), else fall back to the
 * generic formatter. Raw ids should never face the reviewer.
 */
const display = (value: unknown, options: SelectOption[] = []): string =>
  optionLabel(value, options) ?? formatValue(value);

/**
 * +added / −removed summary for a list-valued change (multiselects, structured
 * arrays), computed against the reviewer's current selection so it tracks
 * inline edits. One item per line — list data reads as a list, not prose.
 */
const ListDelta: React.FC<{
  before: unknown;
  current: unknown;
  options?: SelectOption[];
}> = ({ before, current, options }) => {
  const beforeItems = (Array.isArray(before) ? before : []).map((v) =>
    display(v, options)
  );
  const currentItems = (Array.isArray(current) ? current : []).map((v) =>
    display(v, options)
  );
  const added = currentItems.filter((v) => !beforeItems.includes(v));
  const removed = beforeItems.filter((v) => !currentItems.includes(v));
  if (added.length === 0 && removed.length === 0) return null;
  return (
    <ul className="tw:m-0 tw:mb-1 tw:list-none tw:p-0 tw:text-[13px]">
      {added.map((v) => (
        <li
          key={`add-${v}`}
          className="tw:break-words tw:text-[color:var(--success-300)]"
        >
          +{v}
        </li>
      ))}
      {removed.map((v) => (
        <li
          key={`del-${v}`}
          className="tw:break-words tw:text-[color:var(--text-weak)] tw:line-through tw:decoration-[var(--error-400)]"
        >
          −{v}
        </li>
      ))}
    </ul>
  );
};

/**
 * Editor for an object-valued field (editKind 'object', e.g. weather): one
 * labeled row per key, each with its own inline diff / inline editor. Edits
 * merge back into the whole object so the field still resumes as one value.
 */
const ObjectDiffRows: React.FC<{
  before: unknown;
  value: unknown;
  label: string;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
}> = ({ before, value, label, onChange, readOnly = false }) => {
  const beforeObj = isPlainObject(before) ? before : undefined;
  const valueObj = isPlainObject(value) ? value : {};
  const keys = Array.from(
    new Set([...Object.keys(beforeObj ?? {}), ...Object.keys(valueObj)])
  );
  if (keys.length === 0) {
    return <span className="tw:text-[color:var(--text-weak)]">—</span>;
  }
  return (
    <div>
      {keys.map((key) => {
        const keyBefore =
          beforeObj && key in beforeObj
            ? asEditableText(beforeObj[key])
            : undefined;
        const current = asEditableText(valueObj[key]);
        const editKind: 'text' | 'textarea' =
          (keyBefore ?? '').length > 80 ||
          current.length > 80 ||
          current.includes('\n')
            ? 'textarea'
            : 'text';
        const setKey = (v: string) => onChange({ ...valueObj, [key]: v });
        return (
          <div key={key} className="tw:mb-2">
            <div className="tw:mb-0.5 tw:text-[12px] tw:text-[color:var(--text-weak)]">
              {humanizeFieldName(key)}
            </div>
            {keyBefore !== undefined ? (
              <InlineDiffText
                before={keyBefore}
                value={current}
                editKind={editKind}
                label={`${label} — ${humanizeFieldName(key)}`}
                onChange={setKey}
                readOnly={readOnly}
              />
            ) : (
              <ConfirmationField
                config={{ editKind }}
                options={[]}
                value={current}
                onChange={(v) => setKey(asEditableText(v))}
                disabled={readOnly}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Array values render one item per line; everything else via display(). */
const ValueDisplay: React.FC<{ value: unknown; options?: SelectOption[] }> = ({
  value,
  options,
}) =>
  Array.isArray(value) ? (
    <ul className="tw:m-0 tw:list-none tw:p-0">
      {value.map((v, i) => (
        <li
          key={`val-${i}`}
          className="tw:break-words tw:text-[color:var(--text-white)]"
        >
          {display(v, options)}
        </li>
      ))}
    </ul>
  ) : (
    <span className="tw:break-words tw:text-[color:var(--text-white)]">
      {display(value, options)}
    </span>
  );

interface Props {
  item: PendingConfirmation;
  /** The unit under review; a set `field` narrows the card to that one field. */
  unit: ReviewUnit;
  /** 1-based position of this unit among its confirmation's field units. */
  fieldOrdinal?: number;
  /** Total field units of this confirmation (split confirmations only). */
  fieldCount?: number;
  index: number;
  total: number;
  cycle: number;
  state?: ItemState;
  /** Render-time id→name option lists keyed by FieldConfig.dynamicOptions. */
  optionSets: Record<string, SelectOption[]>;
  /**
   * Whether responding is available — a response submits as a reject-with-
   * message, so it must be disabled when the confirmation's allowed_decisions
   * excludes 'reject' (otherwise typed text would submit an unsendable
   * reject).
   */
  canRespond?: boolean;
  /** Non-owner viewer: every control renders disabled and edits are ignored. */
  readOnly?: boolean;
  onEdit: (field: string, value: unknown) => void;
  onRespond: (text: string) => void;
}

export const ChangeReview: React.FC<Props> = ({
  item,
  unit,
  fieldOrdinal,
  fieldCount,
  index,
  total,
  cycle,
  state,
  optionSets,
  canRespond = true,
  readOnly = false,
  onEdit: onEditProp,
  onRespond,
}) => {
  const onEdit = readOnly ? () => undefined : onEditProp;
  const entity =
    typeof item.args?.entity === 'string'
      ? (item.args.entity as string)
      : undefined;

  const resolveOptions = (config: FieldConfig): SelectOption[] =>
    config.dynamicOptions
      ? (optionSets[config.dynamicOptions] ?? [])
      : (config.options ?? []);

  const editValue = (field: string): unknown =>
    state?.edits && field in state.edits
      ? state.edits[field]
      : getArgValue(item.args, item.tool_name, field);

  const isFieldUnit = unit.field !== undefined;
  const fieldRows = useMemo(
    () =>
      (item.preview?.diff ?? [])
        .filter((d) => !isFieldUnit || d.field === unit.field)
        .map((d) => ({
          field: d.field,
          before: d.before,
          after: d.after,
          label: getFieldLabel(item.tool_name, entity, d.field),
          config: getFieldConfig(item.tool_name, entity, d.field, d.after),
        })),
    [item, entity, isFieldUnit, unit.field]
  );

  const title = isFieldUnit
    ? getFieldLabel(item.tool_name, entity, unit.field!)
    : item.preview?.action || item.tool_name;

  return (
    <div>
      <div className="tw:text-[12px] tw:text-[color:var(--text-weak)]">
        Change {index + 1} of {total}
        {cycle > 1 && (
          <span className="tw:text-[color:var(--info-400)]"> · re-review</span>
        )}
      </div>
      <h3
        className={`${isFieldUnit ? 'tw:mb-1' : 'tw:mb-2.5'} tw:mt-0.5 tw:text-[16px] tw:font-medium tw:text-[color:var(--text-white)]`}
      >
        {title}
      </h3>
      {isFieldUnit && (
        <div className="tw:mb-2.5 tw:text-[12px] tw:text-[color:var(--text-weak)]">
          Part of: {item.preview?.action || item.tool_name}
          {fieldOrdinal !== undefined && fieldCount !== undefined && (
            <>
              {' '}
              · field {fieldOrdinal} of {fieldCount}
            </>
          )}
        </div>
      )}
      {item.preview?.irreversible && (
        <span className="badge bg-danger mb-2">Irreversible</span>
      )}

      {fieldRows.length > 0 ? (
        <div>
          {fieldRows.map((row) => (
            <div
              key={row.field}
              className="tw:py-2.5 tw:[&:not(:first-child)]:border-t tw:[&:not(:first-child)]:border-[color:var(--border-neutral)]"
            >
              {!isFieldUnit && (
                <div className="tw:mb-1 tw:text-[12px] tw:font-semibold tw:text-[color:var(--text-weak)]">
                  {row.label}
                </div>
              )}
              {row.config.editKind === 'object' ? (
                <div className="tw:mt-1.5">
                  <ObjectDiffRows
                    before={row.before}
                    value={editValue(row.field)}
                    label={row.label}
                    onChange={(next) => onEdit(row.field, next)}
                    readOnly={readOnly}
                  />
                </div>
              ) : row.config.editKind === 'readonly' ? (
                <>
                  {row.before !== undefined &&
                  (Array.isArray(row.before) || Array.isArray(row.after)) ? (
                    <ListDelta
                      before={row.before}
                      current={row.after}
                      options={resolveOptions(row.config)}
                    />
                  ) : (
                    row.before !== undefined &&
                    formatValue(row.before) !== formatValue(row.after) && (
                      <div className="tw:break-words tw:text-[14px] tw:leading-[1.55] tw:text-[color:var(--text-white)]">
                        <span className="tw:text-[color:var(--text-weak)] tw:line-through tw:decoration-[var(--error-400)]">
                          {formatValue(row.before)}
                        </span>
                        <span className="tw:text-[color:var(--text-weak)]">
                          {' '}
                          →{' '}
                        </span>
                        <span className="tw:text-[color:var(--success-300)]">
                          {formatValue(row.after)}
                        </span>
                      </div>
                    )
                  )}
                  <ValueDisplay
                    value={row.after}
                    options={resolveOptions(row.config)}
                  />
                </>
              ) : (row.config.editKind === 'text' ||
                  row.config.editKind === 'textarea') &&
                row.before !== undefined ? (
                <div className="tw:mt-1.5">
                  <InlineDiffText
                    before={asEditableText(row.before)}
                    value={asEditableText(editValue(row.field))}
                    editKind={row.config.editKind}
                    label={row.label}
                    onChange={(value) => onEdit(row.field, value)}
                    readOnly={readOnly}
                  />
                </div>
              ) : (
                <>
                  {row.config.editKind === 'multiselect' &&
                    row.before !== undefined && (
                      <ListDelta
                        before={row.before}
                        current={editValue(row.field)}
                        options={resolveOptions(row.config)}
                      />
                    )}
                  {row.config.editKind === 'select' &&
                    row.before !== undefined &&
                    formatValue(row.before) !== formatValue(row.after) && (
                      <div className="tw:mb-1 tw:text-[13px] tw:text-[color:var(--text-weak)]">
                        was: {display(row.before, resolveOptions(row.config))}
                      </div>
                    )}
                  <div className="tw:mt-1.5">
                    <ConfirmationField
                      config={row.config}
                      options={resolveOptions(row.config)}
                      value={editValue(row.field)}
                      onChange={(value) => onEdit(row.field, value)}
                      disabled={readOnly}
                    />
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="tw:text-[color:var(--text-weak)]">
          No fields to edit — accept to proceed.
        </p>
      )}

      {!!item.preview?.affectedEntities?.length && (
        <p className="tw:mt-3 tw:text-[12px] tw:text-[color:var(--text-weak)]">
          Affects:{' '}
          {item.preview.affectedEntities
            .map((e) => `${e.kind} "${e.label}"`)
            .join(', ')}
        </p>
      )}

      {canRespond && (
        <div className="tw:mt-3.5">
          <label className="form-label tw:text-[13px] tw:text-[color:var(--text-white)]">
            Respond to the agent{' '}
            <span className="tw:text-[color:var(--text-weak)]">(optional)</span>
          </label>
          <StylishNewTextArea
            rows={2}
            value={state?.respondText ?? ''}
            disableLengthMessage
            disabled={readOnly}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onRespond(e.target.value)
            }
            placeholder={
              isFieldUnit
                ? 'Type here to send this field back for revision — your edits above are included as context.'
                : 'Type here to send this back for revision — your edits above are included as context.'
            }
          />
        </div>
      )}
    </div>
  );
};

export default ChangeReview;
